import {StaffingPlanFormModal} from "../staffing-plan/StaffingPlanFormModal";
import React, {useEffect, useMemo, useState} from "react";
import {AuthorizationInfo} from "../domain";
import {StaffingAddendumType} from "../staffing-plan/domain";
import {ClientReportModal} from "../ClientReportModal";
import {useObservable} from "../../../rxsupport";
import {useAuthStaffingPlan} from "./mutation/useAuthStaffingPlan";
import {useAuthConsultWithCCM} from "./mutation/useAuthConsultWithCCM";
import {ConsultWithCCMFormModal} from "../offer-at-risk/consult-with-ccm/ConsultWithCCMFormModal";
import _ from "lodash";
import {TreatmentHoldFromAuthModal} from "../offer-at-risk/family-init-hold/TreatmentHoldFromAuthModal";
import {useAuthTreatmentHold} from "./mutation/useAuthTreatmentHold";
import {useAuthNoContact} from "./mutation/useAuthNoContact";
import {NoContactFormAuthModal} from "../offer-at-risk/no-contact/NoContactFormAuthModal";
import {useAuthReviewNeeded} from "./mutation/useAuthReviewNeeded";
import {AuthReviewNeededFormAuthModal} from "../offer-at-risk/auth-review/AuthReviewNeededFormAuthModal";
import {
    ClientTransferReturnRequestFormAuthModal
} from "../offer-at-risk/client-return-transfer/ClientTransferReturnRequestFormAuthModal";
import {useAuthClientReturnsTransfer} from "./mutation/useAuthClientReturnsTransfer";
import {AuthClientReportModalState} from "../ClientProfileAuthorizationModal";
import {
    AnticipatedDischargeDateUpdateFormModal
} from "../offer-at-risk/anticipated-discharge-update/AnticipatedDischargeDateUpdateFormModal";
import {useAuthAnticipatedDischargeDateUpdate} from "./mutation/useAuthAnticipatedDischargeDateUpdate";
import { MissingDocumentationFormAuthModal } from "../offer-at-risk/missing-documentation/MissingDocumentationFormAuthModal";
import { useAuthMissingDocumentation } from "./mutation/useAuthMissingDocumentation";
import { ClientFamilyConcernFormModal } from "../offer-at-risk/client-family-concern/ClientFamilyConcernFormModal";
import { useAuthClientFamilyConcern } from "./mutation/useAuthClientFamilyConcern";
import {InsuranceNotificationFormModal} from "../offer-at-risk/insurance-notification/InsuranceNotificationFormModal";
import {useAuthInsuranceNotification} from "./mutation/useAuthInsuranceNotification";
import {ReportStatusInquiryFormModal} from "../offer-at-risk/report-status-inquiry/ReportStatusInquiryFormModal";
import {useAuthReportStatusInquiry} from "./mutation/useAuthReportStatusInquiry";

type Props = {
    authorization: AuthorizationInfo;
    clientGuid: string;
    authState: AuthClientReportModalState;
    refetch?: () => void;
}

export function AuthReportModals({
                                     authorization,
                                     clientGuid,
                                     authState,
                                     refetch,
                                 }: Props) {

    const {
        showStaffingPlan$,
        showConsultWithCCM$,
        showAddendum$,
        showTreatmentHold$,
        showNoContact$,
        showAuthReviewNeeded$,
        showClientReturnTransferRequest$,
        showAnticipatedDischargeDateUpdate$,
        showMissingDocumentation$,
        showClientFamilyConcern$,
        showInsuranceNotification$,
        showReportStatusInquiry$,
    } = authState;

    const {submitStaffingPlan} = useAuthStaffingPlan(authorization.guid);
    const {submitConsultWithCCM} = useAuthConsultWithCCM(authorization.guid);
    const {submitTreatmentHold} = useAuthTreatmentHold(authorization.guid);
    const {submitAuthNoContact} = useAuthNoContact(authorization.guid);
    const {submitAuthReviewNeeded} = useAuthReviewNeeded(authorization.guid);
    const {submitClientReturnsTransferReq} = useAuthClientReturnsTransfer(authorization.guid);
    const {submitAuthAnticipatedDischargeDateUpdate} = useAuthAnticipatedDischargeDateUpdate(authorization.guid);
    const {submitMissingDocumentation} = useAuthMissingDocumentation(authorization.guid);
    const {submitClientFamilyConcern} = useAuthClientFamilyConcern(authorization.guid);
    const {submitAuthInsuranceNotification} = useAuthInsuranceNotification(authorization.guid);
    const {submitReportStatusInquiry} = useAuthReportStatusInquiry(authorization.guid);

    const showingAddendumModal = useObservable(showAddendum$, false);
    const [treatmentHoldStaffingFlow, setTreatmentHoldStaffingFlow] = useState(false);

    useEffect(() => {
        const sub = showStaffingPlan$.subscribe((visible) => {
            if (!visible) {
                setTreatmentHoldStaffingFlow(false);
            }
        });
        return () => sub.unsubscribe();
    }, [showStaffingPlan$]);

    const addendumStaffingAction = useMemo(() => ({
        visible: showingAddendumModal,
        addendumType: showingAddendumModal ? 'ABA_SHORT_ADDENDUM' : ''
    }), [showingAddendumModal]);

    return (
        <>
            <StaffingPlanFormModal
                visible$={showStaffingPlan$}
                authorization={authorization}
                onFinished={submitStaffingPlan}
                isAuth
                treatmentHoldStaffingFlow={treatmentHoldStaffingFlow}
            />

            <ClientReportModal
                showModal={addendumStaffingAction.visible}
                hideModalFunc={() => showAddendum$.next(false)}
                clientGuid={clientGuid}
                defaultAuth={authorization}
                staffingAddendumReport={{
                    type: addendumStaffingAction.addendumType as StaffingAddendumType,
                    onFinished: _.noop
                }}
                refetch={refetch}
            />

            <ConsultWithCCMFormModal
                                     visible$={showConsultWithCCM$}
                                     onFinished={submitConsultWithCCM}/>
            <AnticipatedDischargeDateUpdateFormModal
                visible$={showAnticipatedDischargeDateUpdate$}
                onFinished={submitAuthAnticipatedDischargeDateUpdate}
            />
            <TreatmentHoldFromAuthModal
                visible$={showTreatmentHold$}
                authorization={authorization}
                onFinished={submitTreatmentHold}
                onRedirectToStaffingPlan={() => {
                    setTreatmentHoldStaffingFlow(true);
                    showStaffingPlan$.next(true);
                }}
            />
            <NoContactFormAuthModal visible$={showNoContact$} authorization={authorization}
                                    onFinished={submitAuthNoContact}/>
            <AuthReviewNeededFormAuthModal visible$={showAuthReviewNeeded$}
                                           onFinished={submitAuthReviewNeeded}/>
            <ClientTransferReturnRequestFormAuthModal
                visible$={showClientReturnTransferRequest$}
                authorization={authorization}
                onFinished={submitClientReturnsTransferReq}
            />
            <MissingDocumentationFormAuthModal visible$={showMissingDocumentation$}
                                               onFinished={submitMissingDocumentation}/>
            <ClientFamilyConcernFormModal
                visible$={showClientFamilyConcern$}
                onFinished={submitClientFamilyConcern}
            />
            <InsuranceNotificationFormModal
                authorization={authorization}
                visible$={showInsuranceNotification$}
                onFinished={submitAuthInsuranceNotification}
            />
            <ReportStatusInquiryFormModal
                authorization={authorization}
                visible$={showReportStatusInquiry$}
                onFinished={submitReportStatusInquiry}
            />
        </>
    );
}
